import Box from '@mui/material/Box'
import LinearProgress from '@mui/material/LinearProgress'
import { useTheme } from '@mui/material/styles'
import { get } from 'lodash'
import PropTypes from 'prop-types'
import React, { useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import CachedDirectionsDeckLayer from 'components/common/map/CachedDirectionsDeckLayer'
import useCachedDirectionsLayers from 'components/common/map/useCachedDirectionsLayers'
import { useRouteContainerFeaturesForDate } from 'components/common/map/useRouteContainerFeaturesForDate'
import { useWebGLSupport } from 'components/common/map/webgl/useWebGLSupport'
import { getSlicedStops } from 'components/configured-service/tabs/MapRoutingTab/utils/routeUtils'
import CommonSelectStopsToMoveMap from 'components/route-manager/BulkActions/common/SelectStopsToMoveMap/CommonSelectStopsToMoveMap'
import Polyline from 'components/route-manager/RouteManagerMapV2/shapes/Polyline'
import { formatStopFeatures } from 'utils/route'
import { buildSequenceLine } from 'utils/routeStopOperations'

const defaultFormValues = {}

const ScheduleStopMap = ({
  routeGeojsonData,
  toBeAssignedGeojsonData,
  selectedRoute,
  rows = [],
  routes: routesMeta,
  loading,
  mapRef,
  onStopClick,
  fitBounds = false,
  onFitBoundsDone,
  serviceDate = null,
}) => {
  const theme = useTheme()
  const methods = useForm({ defaultValues: defaultFormValues })

  // Container + driver-path data for the selected route — the driver path is an
  // independent historical reference (CommonSelectStopsToMoveMap owns the date
  // picker), so we only supply the route + the dialog's service date as defaults.
  const driverRouteIds = useMemo(() => (selectedRoute ? [selectedRoute] : []), [selectedRoute])
  const containerFeatures = useRouteContainerFeaturesForDate({ routeIds: driverRouteIds, date: serviceDate })

  const routeColor = useMemo(() => {
    const route = routesMeta?.find(r => r.id === selectedRoute)
    return get(route, 'color') || theme.palette.primary.main
  }, [routesMeta, selectedRoute, theme])

  const allMarkerFeatures = useMemo(() => {
    const coloredGeojsonData = Array.isArray(routeGeojsonData)
      ? routeGeojsonData.map(feature => ({
          ...feature,
          properties: { ...get(feature, 'properties', {}), color: routeColor },
        }))
      : []

    const routeFeatures = formatStopFeatures(coloredGeojsonData, theme)

    const toBeAssignedFeatures = Array.isArray(toBeAssignedGeojsonData)
      ? toBeAssignedGeojsonData.map(feature => ({
          ...feature,
          properties: {
            ...get(feature, 'properties', {}),
            icon: 'to-be-assigned-marker',
            position: '',
          },
        }))
      : []

    return [...routeFeatures, ...toBeAssignedFeatures]
  }, [routeGeojsonData, toBeAssignedGeojsonData, theme, routeColor])

  // Sequence line reflects the user's currently-previewed ordering (including
  // the to-be-assigned stop at its chosen position) — same as MapRoutingTab.
  const mapRoutes = useMemo(() => {
    if (!selectedRoute || allMarkerFeatures.length === 0) return []
    return [
      {
        id: selectedRoute,
        markers: allMarkerFeatures,
        sequenceLine: buildSequenceLine(rows),
        color: routeColor,
      },
    ]
  }, [selectedRoute, allMarkerFeatures, routeColor, rows])

  const stopsById = useMemo(() => {
    const map = {}
    if (Array.isArray(rows)) {
      rows.forEach(row => {
        if (row.id || row.workOrderId) {
          map[row.id || row.workOrderId] = row
        }
      })
    }
    return map
  }, [rows])

  const fitCoordinates = useMemo(
    () =>
      allMarkerFeatures
        .map(f => get(f, 'geometry.coordinates'))
        .filter(c => Array.isArray(c) && c.length === 2 && Number.isFinite(c[0]) && Number.isFinite(c[1])),
    [allMarkerFeatures]
  )

  const handleMarkerClick = React.useCallback(
    feature => {
      const properties = get(feature, 'properties', {})
      if (properties.icon === 'to-be-assigned-marker') return
      const position = get(properties, 'position')
      if (position && onStopClick) {
        onStopClick(Number(position))
      }
    },
    [onStopClick]
  )

  // `getSlicedStops` recognizes `toBeInserted` (route-manager) in addition to
  // `isPending` (configured-services) so the 25-stop window centers correctly.
  const slicedStops = useMemo(() => getSlicedStops(rows, null), [rows])
  const webglSupported = useWebGLSupport()
  const { encodedPath } = useCachedDirectionsLayers({ stops: slicedStops })

  const directionsChildren = useMemo(() => {
    if (!encodedPath) return null
    if (webglSupported) {
      return <CachedDirectionsDeckLayer color={theme.palette.primary.main} encodedPath={encodedPath} />
    }
    return (
      <Polyline
        showArrows
        encodedPath={encodedPath}
        strokeColor={theme.palette.primary.main}
        strokeOpacity={0.8}
        strokeWeight={4}
        zIndex={1}
      />
    )
  }, [webglSupported, encodedPath, theme.palette.primary.main])

  return (
    <Box height="100%" position="relative" width="100%">
      {loading && (
        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 3 }}>
          <LinearProgress />
        </Box>
      )}
      <FormProvider {...methods}>
        <CommonSelectStopsToMoveMap
          containerFeatures={containerFeatures}
          driverRouteIds={driverRouteIds}
          driverServiceDate={serviceDate}
          enableBoxSelect={false}
          fitBounds={fitBounds}
          fitCoordinates={fitCoordinates}
          mapChildren={directionsChildren}
          mapRef={mapRef}
          polylineProps={{ showArrows: true, strokeWeight: 3, strokeOpacity: 0.85 }}
          routes={mapRoutes}
          stopsById={stopsById}
          onFitBoundsDone={onFitBoundsDone}
          onMarkerClick={onStopClick ? handleMarkerClick : undefined}
        />
      </FormProvider>
    </Box>
  )
}

ScheduleStopMap.propTypes = {
  routeGeojsonData: PropTypes.array,
  toBeAssignedGeojsonData: PropTypes.array,
  selectedRoute: PropTypes.string,
  rows: PropTypes.array,
  routes: PropTypes.array,
  loading: PropTypes.bool,
  mapRef: PropTypes.shape({ current: PropTypes.object }),
  onStopClick: PropTypes.func,
  fitBounds: PropTypes.bool,
  onFitBoundsDone: PropTypes.func,
  serviceDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.oneOf([null])]),
}

export default ScheduleStopMap
